import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { List } from 'react-virtualized';
import 'react-virtualized/styles.css';
import {
    Input,
    InputGroup,
    InputLeftElement,
    Box,
    Text,
    useOutsideClick,
    VStack,
    Spinner,
    Flex,
} from '@chakra-ui/react';
import { SearchIcon, ChevronDownIcon } from '@chakra-ui/icons';

/**
 * VirtualizedSelect - A high-performance select component for large datasets
 * Uses react-virtualized for virtualization to handle 100k+ options efficiently
 * 
 * @param {Array} options - Array of options to display
 * @param {string} value - Currently selected value
 * @param {Function} onChange - Callback when selection changes
 * @param {Function} getOptionLabel - Function to get display label from option
 * @param {Function} getOptionValue - Function to get value from option
 * @param {string} placeholder - Placeholder text
 * @param {boolean} isDisabled - Whether select is disabled
 * @param {number} itemHeight - Height of each option in pixels
 * @param {number} maxHeight - Maximum height of dropdown
 */
const VirtualizedSelect = ({
    options = [],
    value,
    onChange,
    getOptionLabel = (option) => option.label || option,
    getOptionValue = (option) => option.value || option,
    placeholder = 'Select...',
    isDisabled = false,
    itemHeight = 40,
    maxHeight = 300,
    darkMode = false,
    isLoading = false,
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const containerRef = useRef(null);
    const listRef = useRef(null);

    // Find selected option for display
    const selectedOption = useMemo(() => {
        return options.find(opt => getOptionValue(opt) === value);
    }, [options, value, getOptionValue]);

    // Filter options based on search - enhanced to search within number ranges
    const filteredOptions = useMemo(() => {
        if (!searchText) return options;
        
        const searchLower = searchText.toLowerCase();
        const searchTrimmed = searchText.trim();
        
        return options.filter(option => {
            const label = getOptionLabel(option);
            const labelStr = String(label).toLowerCase();
            
            // Search in the formatted label (e.g., "+66243043 84 - +66243043 84")
            if (labelStr.includes(searchLower)) return true;
            
            // Check if search term is numeric and falls within the number range
            if (/^\+?\d+$/.test(searchTrimmed)) {
                // Remove leading + if present
                const searchNumber = searchTrimmed.replace(/^\+/, '');
                const searchNumeric = BigInt(searchNumber);
                
                if (option.first && option.last) {
                    try {
                        const firstNum = BigInt(String(option.first).replace(/^\+/, ''));
                        const lastNum = BigInt(String(option.last).replace(/^\+/, ''));
                        
                        // Check if search number is within range (inclusive)
                        if (searchNumeric >= firstNum && searchNumeric <= lastNum) {
                            return true;
                        }
                    } catch (e) {
                        // Continue with other checks if BigInt conversion fails
                    }
                }
            }
            
            // Also search in individual fields like first, last, company name, etc.
            if (option.first && String(option.first).toLowerCase().includes(searchLower)) return true;
            if (option.last && String(option.last).toLowerCase().includes(searchLower)) return true;
            if (option.companyName && String(option.companyName).toLowerCase().includes(searchLower)) return true;
            if (option.wholesalerName && String(option.wholesalerName).toLowerCase().includes(searchLower)) return true;
            if (option.partnerName && String(option.partnerName).toLowerCase().includes(searchLower)) return true;
            
            return false;
        });
    }, [options, searchText, getOptionLabel]);

    // Close dropdown when clicking outside
    useOutsideClick({
        ref: containerRef,
        handler: () => setIsOpen(false),
    });

    // Handle option selection
    const handleSelect = useCallback((option) => {
        onChange(getOptionValue(option));
        setIsOpen(false);
        setSearchText('');
    }, [onChange, getOptionValue]);

    // Reset search when dropdown opens
    useEffect(() => {
        if (isOpen) {
            setSearchText('');
        }
    }, [isOpen]);

    // Render each row in the virtual list
    const rowRenderer = ({ index, key, style }) => {
        const option = filteredOptions[index];
        const optionValue = getOptionValue(option);
        const isSelected = optionValue === value;
        
        return (
            <Box
                key={key}
                style={style}
                px={3}
                py={2}
                cursor="pointer"
                bg={
                    isSelected
                        ? darkMode
                            ? '#006eff'
                            : 'blue.50'
                        : 'transparent'
                }
                color={darkMode ? 'white' : 'black'}
                _hover={{
                    bg: darkMode ? '#2d3748' : 'gray.100',
                }}
                onClick={() => handleSelect(option)}
                fontSize="sm"
            >
                {getOptionLabel(option)}
            </Box>
        );
    };

    const dropdownHeight = Math.min(
        filteredOptions.length * itemHeight,
        maxHeight
    );

    return (
        <Box ref={containerRef} position="relative" {...props}>
            {/* Selected value display */}
            <Box
                border="1px solid"
                borderColor={darkMode ? '#444950' : 'gray.300'}
                borderRadius="md"
                px={3}
                py={2}
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
                opacity={isDisabled ? 0.5 : 1}
                bg={darkMode ? '#23272F' : 'white'}
                color={darkMode ? 'white' : 'black'}
                onClick={() => !isDisabled && setIsOpen(!isOpen)}
                fontSize="sm"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
            >
                <Text isTruncated>
                    {selectedOption
                        ? getOptionLabel(selectedOption)
                        : placeholder}
                </Text>
                {isLoading ? (
                    <Spinner size="xs" />
                ) : (
                    <ChevronDownIcon 
                        boxSize={5}
                        color={darkMode ? 'gray.400' : 'gray.500'}
                    />
                )}
            </Box>

            {/* Dropdown */}
            {isOpen && !isDisabled && (
                <VStack
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    mt={1}
                    bg={darkMode ? '#23272F' : 'white'}
                    border="1px solid"
                    borderColor={darkMode ? '#444950' : 'gray.300'}
                    borderRadius="md"
                    boxShadow="lg"
                    zIndex={1000}
                    spacing={0}
                    maxH={maxHeight + 50}
                >
                    {/* Search input */}
                    <Box p={2} w="100%" borderBottom="1px solid" borderColor={darkMode ? '#444950' : 'gray.200'} bg={darkMode ? '#23272F' : 'white'}>
                        <InputGroup size="sm">
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color={darkMode ? 'gray.400' : 'gray.500'} />
                            </InputLeftElement>
                            <Input
                                placeholder="Search"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                onKeyPress={(event) => {
                                    // Only allow numbers and + sign
                                    if (!/[0-9+]/.test(event.key)) {
                                        event.preventDefault();
                                    }
                                }}
                                autoFocus
                                background={darkMode ? '#1a1d24' : 'white'}
                                color={darkMode ? 'white' : 'black'}
                                borderColor={darkMode ? '#444950' : 'gray.300'}
                                _placeholder={{ color: darkMode ? 'gray.400' : 'gray.500' }}
                                _focus={{
                                    borderColor: darkMode ? '#006eff' : 'blue.500',
                                    boxShadow: darkMode ? '0 0 0 1px #006eff' : '0 0 0 1px #3182ce'
                                }}
                            />
                        </InputGroup>
                    </Box>

                    {/* Options count - always show when searching or when there are multiple results */}
                    {(searchText || filteredOptions.length > 1) && (
                        <Box px={3} py={1} w="100%" fontSize="xs" color={darkMode ? 'gray.400' : 'gray.500'}>
                            Showing {filteredOptions.length.toLocaleString()} result{filteredOptions.length !== 1 ? 's' : ''}
                        </Box>
                    )}

                    {/* Virtualized list */}
                    {filteredOptions.length > 0 ? (
                        <List
                            ref={listRef}
                            height={dropdownHeight}
                            rowCount={filteredOptions.length}
                            rowHeight={itemHeight}
                            rowRenderer={rowRenderer}
                            width={containerRef.current?.offsetWidth || 300}
                            overscanRowCount={5}
                        />
                    ) : (
                        <Flex
                            justify="center"
                            align="center"
                            py={8}
                            color="gray.500"
                            fontSize="sm"
                        >
                            No options found
                        </Flex>
                    )}
                </VStack>
            )}
        </Box>
    );
};

export default VirtualizedSelect;
